import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/layouts/ClientLayout';
import {
  TrendingUp,
  CheckCircle,
  Calendar,
  AlertCircle,
  Eye,
  MessageSquare,
  Clock
} from 'lucide-react';
import { Progress } from '../../components/ui/progress';
import { Button } from '../../components/ui/button';
import { Link } from 'react-router-dom';

const ClientDashboard = () => {
  const { api, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, projectsRes, approvalsRes] = await Promise.all([
        api.get('/stats/client'),
        api.get('/projects'),
        api.get('/approvals/pending'),
      ]);
      setStats(statsRes.data);
      setProjects(projectsRes.data);
      setPendingApprovals(approvalsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ClientLayout>
      <div className="space-y-8" data-testid="client-dashboard">
        {/* Welcome */}
        <div>
          <h1 className="text-2xl font-syne font-bold text-white">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-fp-textMuted mt-1">Here's what's happening with your projects</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: TrendingUp, label: 'Active Projects', value: stats?.projects || 0, color: 'text-blue-400' },
            { icon: CheckCircle, label: 'Pending Approvals', value: stats?.pending_approvals || 0, color: 'text-yellow-400' },
            { icon: Calendar, label: 'Deliverables', value: stats?.deliverables || 0, color: 'text-green-400' },
            { icon: Eye, label: 'This Month', value: 'Active', color: 'text-primary' },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              className="bg-fp-surface border border-white/5 rounded-sm p-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="flex items-center gap-2 mb-2">
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
                <span className="text-fp-textMuted text-sm">{stat.label}</span>
              </div>
              <p className="text-2xl font-syne font-bold text-white">{stat.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Projects Progress */}
        {projects.length > 0 && (
          <div className="bg-fp-surface border border-white/5 rounded-sm p-6">
            <h2 className="font-syne font-bold text-white mb-4">Project Progress</h2>
            <div className="space-y-6">
              {projects.map((project) => (
                <div key={project.id}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white font-medium">{project.name}</h3>
                    <span className="text-primary font-mono">{project.progress || 0}%</span>
                  </div>
                  <Progress value={project.progress || 0} className="h-2 bg-white/10" />
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-fp-textMuted text-xs">
                      Target: {project.target_date || 'Not set'}
                    </span>
                    <span className={`px-2 py-0.5 text-xs rounded ${
                      project.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {project.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Approvals */}
        {pendingApprovals.length > 0 && (
          <div className="bg-fp-surface border border-white/5 rounded-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-syne font-bold text-white">Pending Approvals</h2>
              <Link to="/client/approvals" className="text-primary text-sm hover:underline">
                View All
              </Link>
            </div>
            <div className="space-y-4">
              {pendingApprovals.slice(0, 3).map((approval) => (
                <div
                  key={approval.id}
                  className="flex items-center gap-4 p-4 bg-fp-bg border border-yellow-500/20 rounded-sm"
                >
                  <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">
                      {approval.deliverable?.title || 'Deliverable'}
                    </p>
                    <p className="text-fp-textMuted text-sm">
                      {approval.deliverable?.type} • Requested {new Date(approval.requested_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Link
                    to="/client/approvals"
                    className="btn-primary py-2 px-4 text-sm"
                    data-testid={`review-approval-${approval.id}`}
                  >
                    Review
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-fp-surface border border-white/5 rounded-sm p-6">
          <h2 className="font-syne font-bold text-white mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/client/approvals"
              className="flex items-center gap-3 p-4 bg-fp-bg rounded-sm hover:bg-white/5 transition-colors"
              data-testid="quick-approvals"
            >
              <CheckCircle className="w-5 h-5 text-primary" />
              <span className="text-white">Review Approvals</span>
            </Link>
            <Link
              to="/client/calendar"
              className="flex items-center gap-3 p-4 bg-fp-bg rounded-sm hover:bg-white/5 transition-colors"
              data-testid="quick-calendar"
            >
              <Calendar className="w-5 h-5 text-primary" />
              <span className="text-white">View Calendar</span>
            </Link>
            <a
              href="https://wa.me/917030771071"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 bg-fp-bg rounded-sm hover:bg-white/5 transition-colors"
              data-testid="quick-whatsapp"
            >
              <MessageSquare className="w-5 h-5 text-primary" />
              <span className="text-white">Contact Support</span>
            </a>
          </div>
        </div>
      </div>
    </ClientLayout>
  );
};

export default ClientDashboard;
